import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { useUnsavedGuard } from "./unsaved-guard"

/** The corner close button's accessible name, in the reader's language. */
const CloseLabel = () => {
  const { t } = useTranslation("common")
  return <span className="sr-only">{t("close")}</span>
}

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * A create or edit dialog: closing it (overlay, Esc, X, a `data-dialog-cancel`
   * button) with unsaved changes asks "Descartar alterações?" first. See `unsaved-guard.tsx`.
   */
  guard?: boolean
  /** With `guard`: forces whether there is something to lose (otherwise the fields are compared). */
  dirty?: boolean
  /** Leaves out the corner close button, for a dialog that draws its own (the member preview, F-177). */
  hideClose?: boolean
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, guard = false, dirty, hideClose = false, onEscapeKeyDown, onPointerDownOutside, ...props }, ref) => {
  const unsaved = useUnsavedGuard(guard, dirty)
  const setRef = (el: HTMLDivElement | null) => {
    unsaved.contentProps.ref(el)
    if (typeof ref === "function") ref(el)
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el
  }
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={setRef}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg max-h-[90vh] max-h-[90dvh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-4 sm:p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg overflow-y-auto",
          className
        )}
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement
          if (target.closest('.pac-container')) {
            e.preventDefault()
          }
        }}
        onEscapeKeyDown={(e) => { onEscapeKeyDown?.(e); if (!e.defaultPrevented && guard) unsaved.contentProps.onEscapeKeyDown(e) }}
        onPointerDownOutside={(e) => { onPointerDownOutside?.(e); if (!e.defaultPrevented && guard) unsaved.contentProps.onPointerDownOutside(e) }}
        {...(guard ? {
          onPointerDownCapture: unsaved.contentProps.onPointerDownCapture,
          onKeyDownCapture: unsaved.contentProps.onKeyDownCapture,
          onClickCapture: unsaved.contentProps.onClickCapture
        } : {})}
        {...props}
      >
        {children}
        {!hideClose && <DialogPrimitive.Close ref={unsaved.closeRef} onClick={guard ? unsaved.onCloseClick : undefined} className="absolute right-4 top-4 h-8 w-8 rounded-lg bg-white/50 backdrop-blur-sm hover:bg-white/80 transition-all duration-200 flex items-center justify-center">
          <X className="h-4 w-4" />
          <CloseLabel />
        </DialogPrimitive.Close>}
        {unsaved.prompt}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Room for the close button in the top-right corner, so a long title never runs under it.
      "flex flex-col space-y-1.5 pr-8 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Pinned to the bottom of the scrolling dialog, so the actions stay in view on tall forms.
      "sticky -bottom-4 z-10 -mb-4 flex flex-col-reverse gap-2 border-t border-gray-100 bg-white pb-4 pt-4 sm:-bottom-6 sm:-mb-6 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2 sm:pb-6",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}